/**
 * Resolve the user's membership in the CURRENT household (T30.2).
 *
 * `GET /api/households` marks each membership with `is_current`, computed
 * server-side as `household.id === user.default_household_id`. That flag is the
 * canonical signal — but a user whose `default_household_id` is not set (e.g. a
 * freshly seeded account, or any row predating that column being populated) gets
 * `is_current: null` on every membership, which would leave the gate unable to
 * read the role. So we resolve robustly:
 *
 *   1. Prefer the membership whose id matches the CURRENT household
 *      (`GET /api/household` → `household.id`) — that household IS the session's
 *      household by definition, regardless of the `is_current` flag.
 *   2. Else fall back to the `is_current` flag.
 *   3. Else, if there is exactly one membership, use it (a single-household user
 *      is unambiguously in that household).
 *
 * Returns `undefined` only when there is genuinely no current membership to
 * resolve (no memberships, or several with no current-household id to match).
 */

export interface MembershipLike {
  id: string;
  role: 'manager' | 'member';
  is_current?: boolean | null;
}

export function resolveCurrentMembership<T extends MembershipLike>(
  memberships: readonly T[] | undefined,
  currentHouseholdId: string | undefined,
): T | undefined {
  const list = memberships ?? [];
  if (list.length === 0) return undefined;

  if (currentHouseholdId) {
    const byId = list.find((m) => m.id === currentHouseholdId);
    if (byId) return byId;
  }

  const byFlag = list.find((m) => m.is_current === true);
  if (byFlag) return byFlag;

  if (list.length === 1) return list[0];

  return undefined;
}
